import { useEffect, useRef, useState } from "react";

import BookingSummaryTable from "./components/BookingSummary/BookingSummaryTable";
import BookingsByFamilyStackedBarChart from "./components/BookingsStackedBarChart/BookingsByFamilyStackedBarChart";
import BookingsCompositionStackedBarChart from "./components/BookingsStackedBarChart/BookingsCompositionStackedBarChart";
import BookingsStackedBarChart from "./components/BookingsStackedBarChart/BookingsStackedBarChart";
import CarrSummaryTable from "./components/CarrSummary/CarrSummaryTable";
import CumulativeCarrStackedChart from "./components/CumlativeCarrStackedChart/CumulativeCarrStackedChart";
import CumulativeCarrChart from "./components/CumulativeCarr/CumulativeCarrChart";
import MonthRangePicker from "./components/Fields/MonthRangePicker";
import SingleSelectField from "./components/Fields/SingleSelectField";
import LoadingOverlay from "./components/LoadingOverlay";
import { getDefaultMonthRange } from "./utils/monthRange";

const TOTAL_COMPONENTS = 7;

const groupingOptions = [
    {
        id: "product_group",
        name: "Product Group",
    },
    {
        id: "product",
        name: "Product",
    },
    {
        id: "product_family",
        name: "Product Family",
    },
    {
        id: "business_unit",
        name: "Business Unit",
    },
];

const ReportsTab = (props) => {
    const [monthRange, setMonthRange] = useState(getDefaultMonthRange());
    // Each summary table has its own independent "Group By" selection
    const [bookingGroup, setBookingGroup] = useState(groupingOptions[0]);
    const [carrGroup, setCarrGroup] = useState(groupingOptions[0]);
    const [loadedCount, setLoadedCount] = useState(0);
    // Earliest month (YYYY-MM) with data across all report datasets; limits the picker
    const [minMonth, setMinMonth] = useState(null);
    // Overlay shown while charts/tables re-render after a month range change
    const [isRangeFiltering, setIsRangeFiltering] = useState(false);
    const pendingRange = useRef(null);

    const handleDataStart = (yearMonth) => {
        if (!yearMonth) return;
        setMinMonth((prev) => {
            const next = !prev || yearMonth < prev ? yearMonth : prev;
            console.log("Earliest month/year with data:", next);
            return next;
        });
    };

    // Range changes are applied client-side (no refetch), so re-rendering all charts
    // and tables runs synchronously. Show the spinner first, then apply the new range
    // a frame later so it's painted before the heavy re-render, and hide it once the
    // updated charts and tables have painted.
    const handleMonthRangeChange = (range) => {
        pendingRange.current = range;
        setIsRangeFiltering(true);
    };

    useEffect(() => {
        if (!isRangeFiltering) return undefined;

        let applyFrame;
        let hideFrame;
        const startFrame = requestAnimationFrame(() => {
            applyFrame = requestAnimationFrame(() => {
                setMonthRange(pendingRange.current);
                hideFrame = requestAnimationFrame(() =>
                    requestAnimationFrame(() => setIsRangeFiltering(false))
                );
            });
        });

        return () => {
            cancelAnimationFrame(startFrame);
            if (applyFrame) cancelAnimationFrame(applyFrame);
            if (hideFrame) cancelAnimationFrame(hideFrame);
        };
    }, [isRangeFiltering]);

    // Each "Group By" change refetches only its own table, which shows its own
    // in-place progress bar while reloading (see the summary table components).
    const handleBookingGroupChange = (option) => {
        setBookingGroup(option);
    };

    const handleCarrGroupChange = (option) => {
        setCarrGroup(option);
    };

    const handleComponentLoaded = () => {
        setLoadedCount((prev) => prev + 1);
    };

    const allLoaded = loadedCount >= TOTAL_COMPONENTS;
    const showOverlay = isRangeFiltering;

    const childProps = {
        props,
        onLoad: handleComponentLoaded,
        onDataStart: handleDataStart,
        startMonth: monthRange.startMonth,
        endMonth: monthRange.endMonth,
    };

    return (
        <>
            {!allLoaded && <LoadingOverlay text="Loading reports..." />}
            <div
                className="flex w-full flex-col pt-4"
                style={{ visibility: allLoaded ? "visible" : "hidden" }}
            >
                <div className="sticky top-0 z-[200] -mx-1 bg-white px-1 pb-10 pt-1">
                    {/* Inner wrapper sticks to the right edge during horizontal scroll */}
                    <div className="sticky right-1 ml-auto flex w-fit items-end gap-3">
                        {/* All components fetch full history once and window client-side,
                            so range changes apply instantly without refetching. */}
                        <MonthRangePicker
                            value={monthRange}
                            minMonth={minMonth}
                            onChange={handleMonthRangeChange}
                        />
                    </div>
                </div>
                <div className="relative">
                    {showOverlay && <LoadingOverlay contained text="Updating reports..." />}
                    <BookingSummaryTable
                        {...childProps}
                        group={bookingGroup.id}
                        headerControl={
                            <SingleSelectField
                                label="Group By"
                                options={groupingOptions}
                                selected={bookingGroup}
                                onChange={handleBookingGroupChange}
                            />
                        }
                    />
                    <CarrSummaryTable
                        {...childProps}
                        group={carrGroup.id}
                        headerControl={
                            <SingleSelectField
                                label="Group By"
                                options={groupingOptions}
                                selected={carrGroup}
                                onChange={handleCarrGroupChange}
                            />
                        }
                    />
                    <CumulativeCarrChart {...childProps} />
                    <BookingsStackedBarChart {...childProps} />
                    <BookingsByFamilyStackedBarChart {...childProps} />
                    <BookingsCompositionStackedBarChart {...childProps} />
                    <CumulativeCarrStackedChart {...childProps} />
                </div>
            </div>
        </>
    );
};

export default ReportsTab;
